import { moveItemInArray } from '@angular/cdk/drag-drop';
import { OnDestroy } from '@angular/core';
import { OnInit } from '@angular/core';
import { forwardRef } from '@angular/core';
import { Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { QueryList } from '@angular/core';
import { ViewChildren } from '@angular/core';
import { TemplateRef } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { Input } from '@angular/core';
import { Component } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessor } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ContentEditorSection } from 'app/modules/main/components/content-editor/content-editor-section';
import { ContentEditorSectionFactory } from 'app/modules/main/components/content-editor/content-editor-section.factory';
import { LinkPopupLocalization } from 'app/modules/main/components/content-editor/link-popup-localization';
import { QuillConfiguration } from 'app/modules/main/components/content-editor/quill-configuration';
import { TemporalFlag } from 'app/modules/main/helper/temporal-flag';
import difference from 'lodash-es/difference';
import { QuillEditorComponent } from 'ngx-quill';
import { skip } from 'rxjs/operators';
import { takeWhile } from 'rxjs/operators';

@UntilDestroy()
@Component({
  providers: [{
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ContentEditorComponent),
  }],
  selector: 'app-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ContentEditorComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {

  private static readonly ENTER_EDIT_MODE_DELAY_MS = 2000;
  private static readonly LEAVE_EDIT_MODE_DELAY_MS = 6000;

  @Input() readonly toolbar: TemplateRef<any>;

  @Output() readonly focused = new EventEmitter<boolean>();

  private readonly _editing =
    new TemporalFlag(
      ContentEditorComponent.LEAVE_EDIT_MODE_DELAY_MS,
      ContentEditorComponent.ENTER_EDIT_MODE_DELAY_MS,
    );

  private _disabled: boolean = false;
  private _sections: ContentEditorSection[] = [];

  private initialContents: string[] = [];

  private componentAlive: boolean = true;
  private viewInitialised: boolean = false;

  private onChange: (contents: string[]) => void;
  private onTouched: () => void;

  private set contents(
    contents: string[]
  ) {

    this.destroySections();

    contents.forEach(content => this.append(content, false));

    if (this._sections.length > 0) {
      this.select(this._sections[0]);
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  get editing(): boolean {
    return this._editing.state;
  }

  get singular(): boolean {
    return this._sections.length === 1;
  }

  get sections(): ContentEditorSection[] {
    return this._sections;
  }

  get selectedSection(): ContentEditorSection | undefined {
    return this._sections.find(section => section.selected);
  }

  get quillModules(): any {
    return QuillConfiguration.modules;
  }

  @ViewChildren(QuillEditorComponent)
  private readonly $editorComponents: QueryList<QuillEditorComponent>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly editorSectionFactory: ContentEditorSectionFactory,
    private readonly linkPopupLocalization: LinkPopupLocalization,
  ) {
  }

  ngOnInit(): void {

    setTimeout(() => QuillConfiguration.initialize());

    this.linkPopupLocalization.apply();
  }

  ngAfterViewInit(): void {

    setTimeout(() => {

      this.contents = this.initialContents;

      this.viewInitialised = true;
    });
  }

  ngOnDestroy(): void {

    this.componentAlive = false;

    this.destroySections();
  }

  blur(
    section?: ContentEditorSection
  ): void {

    if (!this.componentAlive) {
      return;
    }

    section = section || this.selectedSection;
    if (!section) {
      return;
    }

    this.leaveEditPhase();

    if (section.focused.value) {
      section.blur();
    }

    this.emitHasFocus();
  }

  focus(
    section?: ContentEditorSection
  ): void {

    if (!this.componentAlive) {
      return;
    }

    section = section || this.selectedSection;
    if (!section) {
      return;
    }

    this.select(section);

    this.leaveEditPhase();

    if (!section.focused.value) {
      section.focus();
    }

    this._sections
      .filter(_section => _section !== section)
      .forEach(_section => _section.blur());

    this.emitHasFocus();
  }

  canUndo(): boolean {
    return !!this.selectedSection?.canUndo();
  }

  canRedo(): boolean {
    return !!this.selectedSection?.canRedo();
  }

  undo(): void {
    this.selectedSection?.undo();
  }

  redo(): void {
    this.selectedSection?.redo();
  }

  enterEditingPhase(): void {

    if (this.singular) {
      return;
    }

    this._editing.enter();
  }

  leaveEditPhase(): void {
    this._editing.leave();
  }

  append(
    content: string = '',
    focus: boolean = true,
    insertIndex: number = undefined
  ): void {

    if (!this.componentAlive) {
      return;
    }

    this.leaveEditPhase();

    const section = this.editorSectionFactory.create(content);

    this.initializeEditorSection(section, (insertIndex || this._sections.length), focus);

    if (content.length) {
      this.notifyChange();
    }
  }

  delete(
    section: ContentEditorSection
  ): void {

    if (!this.componentAlive) {
      return;
    }

    const sectionIndex = this._sections.indexOf(section);

    this._sections =
      this._sections.filter(_section => _section !== section);

    const focusSectionIndex = Math.min(sectionIndex, this._sections.length - 1);

    this.focus(this._sections[focusSectionIndex]);

    this.notifyChange();

    section.destroy();
  }

  duplicate(
    section: ContentEditorSection
  ): void {

    if (!this.componentAlive) {
      return;
    }

    const sectionIndex = this._sections.indexOf(section);

    this.append(section.content.value, true, (sectionIndex + 1));

    this.notifyChange();
  }

  reposition(
    fromIndex: number,
    toIndex: number,
  ): void {

    if (!this.componentAlive) {
      return;
    }

    if (fromIndex === toIndex) {
      return;
    }

    moveItemInArray(this._sections, fromIndex, toIndex);

    this.notifyChange();
  }

  writeValue(
    contents: string | string[]
  ): void {

    if (!this.componentAlive) {
      return;
    }

    contents = [].concat(contents || ['']);

    this.viewInitialised
      ? this.contents = contents
      : this.initialContents = contents;
  }

  registerOnChange(
    fn: (contents: string[]) => void
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(
    fn: () => void
  ): void {
    this.onTouched = fn;
  }

  setDisabledState(
    disabled: boolean
  ): void {

    this._disabled = disabled;

    this._sections.forEach(section =>
      disabled ? section.disable() : section.enable()
    );
  }

  trackById(
    _: number,
    o: { id: string }
  ): string {
    return o.id;
  }

  private destroySections(): void {

    this._sections.forEach(section => section.destroy());
    this._sections = [];
  }

  private initializeEditorSection(
    section: ContentEditorSection,
    insertIndex: number,
    focus: boolean = true
  ): void {

    insertIndex = Math.min(Math.max(insertIndex, 0), this._sections.length);

    this._sections.splice(insertIndex, 0, section);

    const existingEditors = this.$editorComponents.toArray();

    this.changeDetector.detectChanges();

    const newEditors = difference(this.$editorComponents.toArray(), existingEditors);

    if (newEditors.length !== 1) {
      console.warn('Editor for section could not be detected');
      return;
    }

    const sectionAlive = () => this.componentAlive && this._sections.includes(section);

    section.initialize(this.changeDetector, newEditors[0], focus);

    section.content
      .pipe(takeWhile(sectionAlive), skip(1))
      .subscribe(() => this.notifyChange());

    section.focused
      .pipe(takeWhile(sectionAlive))
      .subscribe(focused => focused ? this.focus(section) : this.blur(section));

    section.touched
      .pipe(takeWhile(sectionAlive))
      .subscribe(() => this.notifyTouch());
  }

  private select(
    section: ContentEditorSection
  ): void {

    this._sections
      .filter(_section => _section !== section)
      .forEach(_section => _section.deselect());

    section.select();
  }

  private emitHasFocus(): void {

    const anySectionHasFocus = this._sections.some(section => section.focused.value);

    this.focused.emit(anySectionHasFocus);
  }

  private notifyChange(): void {

    const contents = this._sections.map(section => section.content.value);

    !!this.onChange && this.onChange(contents);
  }

  private notifyTouch(): void {
    !!this.onTouched && this.onTouched();
  }

}
